import type { ReactElement, ReactNode } from 'react';
import { useState } from 'react';
import { Button } from '@savvy-kit/components/button';
import { Calendar } from '@savvy-kit/components/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@savvy-kit/components/popover';
import { cn } from '@savvy-kit/lib/utils';
import { format, isValid } from 'date-fns';
import { CalendarIcon } from 'lucide-react';

const DEFAULT_DISPLAY_FORMAT = 'MM/dd/yyyy';

export type DatePickerTriggerArgs = {
  readonly displayValue: string;
  readonly isOpen: boolean;
};

export type DatePickerProps = {
  /** Currently selected date. `undefined` renders the placeholder. */
  readonly value: Date | undefined;
  /** Called when the user picks (or clears) a date. */
  readonly onChange: (date: Date | undefined) => void;
  /** Called when the popover closes (the field-level blur analog). Fires on close, not on open. */
  readonly onBlur?: () => void;
  /** Called when the popover opens. For callers that must settle conflicting state before a pick. */
  readonly onOpen?: () => void;
  /** When set and a date is selected, renders a Clear button in the popover footer that unsets the value and closes. */
  readonly clearable?: boolean;
  /** Label for the `clearable` button. Defaults to `Clear`. */
  readonly clearLabel?: string;
  /** Placeholder text shown when no date is selected. */
  readonly placeholder?: string;
  /** date-fns format string for the trigger display. Defaults to `MM/dd/yyyy`. */
  readonly displayFormat?: string;
  /** Earliest selectable date (also constrains the year dropdown). */
  readonly minDate?: Date;
  /** Latest selectable date (also constrains the year dropdown). */
  readonly maxDate?: Date;
  /** Month to show when the picker opens with no value set. */
  readonly defaultMonth?: Date;
  readonly disabled?: boolean;
  /** Class name applied to the default trigger button. Ignored when `renderTrigger` is supplied. */
  readonly triggerClassName?: string;
  /** ARIA label for the trigger when no visible label is supplied by the caller. */
  readonly ariaLabel?: string;
  /** Applied to the default trigger button so an external `<label htmlFor>` associates with it. Ignored when `renderTrigger` is supplied. */
  readonly id?: string;
  /** Forwarded as `aria-describedby` on the trigger so an external error/help element is announced. */
  readonly ariaDescribedby?: string;
  /** Forwarded as `aria-invalid` on the trigger so assistive tech announces the error state. */
  readonly ariaInvalid?: boolean;
  /** Forwarded as `aria-required` on the trigger so assistive tech announces the field is required. */
  readonly ariaRequired?: boolean;
  /**
   * Override the default trigger. Consumers return a single element that becomes
   * the popover trigger DOM — Popover's render slot fuses click/focus/aria
   * wiring onto it. Receives the formatted display string and the popover's
   * current open state so the consumer can render their own label.
   */
  readonly renderTrigger?: (args: DatePickerTriggerArgs) => ReactElement;
  /** Optional node rendered inside the popover under the calendar. Useful for a "Clear" or "Reset" affordance. */
  readonly footer?: ReactNode;
  /** Class name forwarded to `PopoverContent`. Overrides the default `w-96 p-0`. */
  readonly popoverContentClassName?: string;
  /** Class name forwarded to `PopoverContent`'s positioner. Use to lift the popover above modal backdrops (e.g. `z-[1400]`). */
  readonly popoverPositionerClassName?: string;
  /** Class name forwarded to the inner `<Calendar>`. Overrides the default `w-96 [--cell-size:--spacing(10)]` (lets consumers ship a compact variant). */
  readonly calendarClassName?: string;
};

export function DatePicker({
  value,
  onChange,
  onBlur,
  onOpen,
  clearable = false,
  clearLabel = 'Clear',
  placeholder = 'MM/DD/YYYY',
  displayFormat = DEFAULT_DISPLAY_FORMAT,
  minDate,
  maxDate,
  defaultMonth,
  disabled = false,
  triggerClassName,
  ariaLabel,
  id,
  ariaDescribedby,
  ariaInvalid,
  ariaRequired,
  renderTrigger,
  footer,
  popoverContentClassName,
  popoverPositionerClassName,
  calendarClassName,
}: DatePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const currentValue = value && isValid(value) ? value : undefined;
  const displayValue = currentValue ? format(currentValue, displayFormat) : placeholder;

  const handleSelect = (next: Date | undefined) => {
    onChange(next);
    setIsOpen(false);
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) onOpen?.();
    else onBlur?.();
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      {renderTrigger ? (
        <PopoverTrigger
          disabled={disabled}
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedby}
          aria-invalid={ariaInvalid}
          aria-required={ariaRequired}
          render={renderTrigger({ displayValue, isOpen })}
        />
      ) : (
        <PopoverTrigger
          disabled={disabled}
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedby}
          aria-invalid={ariaInvalid}
          aria-required={ariaRequired}
          render={
            <button
              type="button"
              id={id}
              className={cn(
                'flex h-10 w-full items-center justify-between rounded-md border border-light bg-white px-3 py-1 text-left text-sm font-normal text-dark shadow-xs outline-none transition-[color,box-shadow] focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-border-light disabled:cursor-not-allowed disabled:opacity-50',
                triggerClassName,
              )}
            />
          }
        >
          <span className={currentValue ? 'text-dark' : 'text-secondary'}>{displayValue}</span>
          <CalendarIcon className="size-4 text-secondary" />
        </PopoverTrigger>
      )}
      <PopoverContent
        align="start"
        className={popoverContentClassName ?? 'w-96 p-0'}
        positionerClassName={popoverPositionerClassName}
      >
        <Calendar
          mode="single"
          selected={currentValue}
          onSelect={handleSelect}
          defaultMonth={currentValue ?? defaultMonth}
          captionLayout="dropdown"
          startMonth={minDate}
          endMonth={maxDate}
          disabled={[...(minDate ? [{ before: minDate }] : []), ...(maxDate ? [{ after: maxDate }] : [])]}
          fixedWeeks
          className={calendarClassName ?? 'w-96 [--cell-size:--spacing(10)]'}
          classNames={{ root: 'border-light' }}
        />
        {clearable && currentValue && (
          <div className="border-t border-light px-3 pt-1 pb-3">
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-[var(--text-secondary)]"
              onClick={() => handleSelect(undefined)}
            >
              {clearLabel}
            </Button>
          </div>
        )}
        {footer}
      </PopoverContent>
    </Popover>
  );
}
